import React, { useMemo } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { ReportingShellContainerBaseConfig } from '../../../base';
import { ChatLayoutContainerProps, ChatLayoutViewProps } from './chatLayoutProps';
import { ChatLayoutController, ChatLayoutLoader } from './chatLayoutInterface';
import { ChatContextProvider, ChatDetailContextValue } from '../detail';
import { useForm } from 'react-hook-form';
import { ChatComposerStateValues } from '../common';

export function createChatLayoutContainer(
    config: ReportingShellContainerBaseConfig & {
        chat: {
            context: ChatContextProvider;
        };
    },
    loader: ChatLayoutLoader,
    controller: ChatLayoutController,
    View: React.FC<ChatLayoutViewProps>
): React.FC<ChatLayoutContainerProps> {
    const {
        kernel: {
            infra: {
                optionParser: { useOptions },
            },
        },
        context: { useContext },
        chat: {
            context: { Provider: ChatContextProvider },
        },
        pubsub: {
            router: { useHandler },
        },
        repository: { assistant },
    } = config;

    return config.state.chat.subscribe(({ ...containerProps }) => {
        const context = useContext();
        const state = {
            chat: config.state.chat.useState(context),
            composer: config.state.composer.useState(context),
        };

        const form = useForm<ChatComposerStateValues>({
            defaultValues: {
                mode: state.composer.value.mode,
                text: state.composer.value.text,
            },
        });

        const data = loader.useLoad(context, containerProps);
        const props = controller.useProps(context, data, {
            thread: {
                onCreate() {
                    console.log('navigating to create thread page');
                    state.chat.onChange({
                        ...state.chat.value,
                        thread: null,
                    });
                },
            },
            overlay: {
                disclosure: useDisclosure({
                    isOpen: state.chat.value.isOpen,
                    onOpen() {
                        state.chat.onChange({
                            ...state.chat.value,
                            isOpen: true,
                        });
                    },
                    onClose() {
                        state.chat.onChange({
                            ...state.chat.value,
                            isOpen: false,
                        });
                    },
                }),
            },
            error: {
                hash: state.chat.value.thread ?? '',
            },
        });

        const contextValue = useMemo(
            (): ChatDetailContextValue => ({
                content: null,
                form,
            }),
            [form]
        );

        return (
            <ChatContextProvider value={contextValue}>
                <View {...props}>{containerProps.children}</View>
            </ChatContextProvider>
        );
    });
}
